// src/redux/admin/reportApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

export const reportApi = createApi({
  reducerPath: "reportApi",
  baseQuery,
  tagTypes: ["Reports", "ReportSchedules", "ReportScheduleMeta", "DataPush", "MoveInSyncPushLogs", "Groups"],
  endpoints: (builder) => ({
    // ✅ Report Preview (JSON)
    getReportPreview: builder.query({
      query: ({
        deviceId,
        type,
        speedLimit,
        startDate,
        endDate,
        groupId,
        deviceIds,
        page,
        pageSize,
        intervalMinutes,
        stopThresholdSeconds,
      }) => {
        const params = { deviceId, type, format: "json" };

        // group summary support (optional)
        if (groupId) params.groupId = groupId;
        if (deviceIds) params.deviceIds = deviceIds;

        if (speedLimit != null) params.speedLimit = speedLimit;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (page != null) params.page = page;
        if (pageSize != null) params.pageSize = pageSize;
        if (intervalMinutes != null) params.intervalMinutes = intervalMinutes;
        if (stopThresholdSeconds != null) params.stopThresholdSeconds = stopThresholdSeconds;

        return { url: "/reports/preview", params };
      },
      providesTags: ["Reports"],
    }),

    // ✅ Report Download (Blob)
    downloadReport: builder.query({
      query: ({
        deviceId,
        type,
        format,
        speedLimit,
        startDate,
        endDate,
        groupId,
        deviceIds,
        intervalMinutes,
        stopThresholdSeconds,
        brandName,
        brandLogoUrl,
      }) => {
        const params = { deviceId, type, format };
        const headers = {};

        if (brandName) headers['x-report-brand-name'] = String(brandName);
        if (brandLogoUrl) headers['x-report-brand-logo-url'] = String(brandLogoUrl);

        // group summary support (optional)
        if (groupId) params.groupId = groupId;
        if (deviceIds) params.deviceIds = deviceIds;

        if (speedLimit != null) params.speedLimit = speedLimit;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (intervalMinutes != null) params.intervalMinutes = intervalMinutes;
        if (stopThresholdSeconds != null) params.stopThresholdSeconds = stopThresholdSeconds;

        return {
          url: "/reports/generate",
          params,
          headers,
          cache: "no-cache",
          responseHandler: async (response) => {
            if (!response.ok) {
              let msg = `Server error: ${response.status}`;
              try {
                const err = await response.json();
                msg = err?.message || err?.msg || msg;
              } catch (_) {}
              throw new Error(msg);
            }
            return await response.blob();
          },
        };
      },
    }),

    getReportScheduleMeta: builder.query({
      query: () => "/reports/schedules/meta",
      providesTags: ["ReportScheduleMeta"],
      transformResponse: (response) => response?.data || response || {},
    }),

    getReportSchedules: builder.query({
      query: () => "/reports/schedules",
      providesTags: (result) =>
        result?.schedules
          ? [
              ...result.schedules.map((schedule) => ({ type: "ReportSchedules", id: schedule._id })),
              { type: "ReportSchedules", id: "LIST" },
            ]
          : [{ type: "ReportSchedules", id: "LIST" }],
      transformResponse: (response) => response?.data || response || { schedules: [] },
    }),

    createReportSchedule: builder.mutation({
      query: (body) => ({
        url: "/reports/schedules",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ReportSchedules", id: "LIST" }],
    }),

    updateReportSchedule: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/reports/schedules/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "ReportSchedules", id: arg.id },
        { type: "ReportSchedules", id: "LIST" },
      ],
    }),

    deleteReportSchedule: builder.mutation({
      query: (id) => ({
        url: `/reports/schedules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ReportSchedules", id: "LIST" }],
    }),

    runReportScheduleNow: builder.mutation({
      query: (id) => ({
        url: `/reports/schedules/${id}/run`,
        method: "POST",
      }),
      invalidatesTags: (res, err, id) => [
        { type: "ReportSchedules", id },
        { type: "ReportSchedules", id: "LIST" },
      ],
    }),

    // ─────────────────────────────────────────────────────────────
    // ✅ GROUPS (NEW) - use same auth headers via baseQuery
    // Expected backend response:
    // 1) { success: true, data: [...] }  OR
    // 2) [...] directly
    // ─────────────────────────────────────────────────────────────
    getGroups: builder.query({
      query: () => ({ url: "/groups" }),
      transformResponse: (res) => {
        if (Array.isArray(res)) return res;
        if (res?.data && Array.isArray(res.data)) return res.data;
        if (res?.success && Array.isArray(res?.data)) return res.data;
        return [];
      },
      providesTags: ["Groups"],
    }),

    // ─────────────────────────────────────────────────────────────
    // ✅ MOVEINSYNC INTEGRATION
    // ─────────────────────────────────────────────────────────────

    pushRangeToMoveInSync: builder.mutation({
      query: ({ imei, fromISO, toISO, limit = 200 }) => ({
        url: "/integrations/moveinsync/push-range",
        method: "POST",
        body: { imei, fromISO, toISO, limit },
      }),
      invalidatesTags: ["MoveInSyncPushLogs"],
    }),

    getMoveInSyncPushLogs: builder.query({
      query: ({ deviceId, startDate, endDate, page = 1, limit = 25, search }) => ({
        url: "/integrations/moveinsync/push-logs",
        params: { deviceId, startDate, endDate, page, limit, search },
      }),
      providesTags: ["MoveInSyncPushLogs"],
    }),

    downloadMoveInSyncPushLogsCsv: builder.query({
      query: ({ deviceId, startDate, endDate, search }) => ({
        url: "/integrations/moveinsync/push-logs/download",
        params: { deviceId, startDate, endDate, search },
        cache: "no-cache",
        responseHandler: async (response) => {
          if (!response.ok) {
            let msg = `Server error: ${response.status}`;
            try {
              const err = await response.json();
              msg = err?.message || err?.msg || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          return await response.blob();
        },
      }),
    }),
  }),
});

export const {
  // Reports
  useLazyGetReportPreviewQuery,
  useLazyDownloadReportQuery,
  useGetReportScheduleMetaQuery,
  useGetReportSchedulesQuery,
  useCreateReportScheduleMutation,
  useUpdateReportScheduleMutation,
  useDeleteReportScheduleMutation,
  useRunReportScheduleNowMutation,

  // Groups (NEW)
  useGetGroupsQuery,

  // MoveInSync
  usePushRangeToMoveInSyncMutation,
  useLazyGetMoveInSyncPushLogsQuery,
  useLazyDownloadMoveInSyncPushLogsCsvQuery,
} = reportApi;
